import { useMemo, useState } from 'react';
import { Download, Search } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Select,
  useToast,
} from '@solara/ui-shared';
import type { AuditEventDto } from '@solara/api-client';
import { useAuditEvents } from '@solara/api-client/react';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

type OutcomeFilter = 'all' | 'success' | 'failure';
type RoleFilter = 'all' | string;
type ResourceFilter = 'all' | string;

export default function AuditLogPage() {
  const { t } = useTranslation('admin');
  const { locale } = useI18n();
  const { toast } = useToast();
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  const [query, setQuery] = useState('');
  const [outcome, setOutcome] = useState<OutcomeFilter>('all');
  const [role, setRole] = useState<RoleFilter>('all');
  const [resource, setResource] = useState<ResourceFilter>('all');

  const auditQuery = useAuditEvents({ limit: 200 });
  // Backend Prisma columns are `occurredAt`/`actorEmail` — normalise to the
  // legacy `at`/`actor` names the table renders.
  const events: AuditEventDto[] = useMemo(
    () =>
      (auditQuery.data ?? []).map((raw) => {
        const e = raw as typeof raw & { occurredAt?: string; actorEmail?: string };
        return {
          ...raw,
          at: e.occurredAt ?? raw.at ?? new Date().toISOString(),
          actor: e.actorEmail ?? raw.actor ?? 'system',
        };
      }),
    [auditQuery.data],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      // Backend audit events have no outcome / actor role fields in the DTO,
      // so those filters are no-ops once the data flows from the API.
      if (resource !== 'all' && e.resource !== resource) return false;
      if (!q) return true;
      return (
        e.action.toLowerCase().includes(q) ||
        e.actor.toLowerCase().includes(q) ||
        (e.ip ?? '').includes(q)
      );
    });
  }, [events, query, resource]);
  void outcome;
  void role;

  function exportCsv() {
    toast({ title: t('audit.exportToast', { count: filtered.length }), tone: 'info' });
  }

  if (auditQuery.isLoading) return <LoadingBlock />;
  if (auditQuery.error) return <ErrorBanner error={auditQuery.error} />;

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('audit.title')}</h1>
          <p className="text-fg-muted">{t('audit.subtitle')}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button onClick={exportCsv} variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            {t('audit.exportCsv')}
          </Button>
          <span className="text-[11px] text-fg-subtle">{t('audit.exportLimit')}</span>
        </div>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 sm:flex-initial">
          <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
          <input
            type="search"
            placeholder={t('audit.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full sm:w-72 h-10 ps-9 pe-3 rounded-md bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          />
        </div>
        <Select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as OutcomeFilter)}
          options={[
            { value: 'all', label: t('audit.filterOutcome') },
            { value: 'success', label: t('audit.table.ok') },
            { value: 'failure', label: t('audit.table.fail') },
          ]}
          containerClassName="w-40"
        />
        <Select
          value={role}
          onChange={(e) => setRole(e.target.value as RoleFilter)}
          options={[
            { value: 'all', label: t('audit.filterRole') },
            { value: 'CustomerAdmin', label: 'Customer Admin' },
            { value: 'Accountant', label: 'Accountant' },
            { value: 'Approver', label: 'Approver' },
            { value: 'Viewer', label: 'Viewer' },
            { value: 'PlatformAdmin', label: 'Platform Admin' },
          ]}
          containerClassName="w-44"
        />
        <Select
          value={resource}
          onChange={(e) => setResource(e.target.value as ResourceFilter)}
          options={[
            { value: 'all', label: t('audit.filterResource') },
            { value: 'invoice', label: 'invoice' },
            { value: 'tenant', label: 'tenant' },
            { value: 'user', label: 'user' },
            { value: 'erp_config', label: 'erp_config' },
            { value: 'api_key', label: 'api_key' },
            { value: 'tdd', label: 'tdd' },
            { value: 'archive', label: 'archive' },
          ]}
          containerClassName="w-40"
        />
      </div>

      <Card>
        <CardBody>
          <div className="text-xs text-fg-muted mb-3">
            {t('audit.results', { count: filtered.length, total: events.length })}
          </div>
          {filtered.length === 0 ? (
            <EmptyState icon={<Search className="h-6 w-6" />} title="No audit events match." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-subtle">
                    <th className="text-start font-medium pb-2">{t('audit.table.when')}</th>
                    <th className="text-start font-medium pb-2">{t('audit.table.actor')}</th>
                    <th className="text-start font-medium pb-2">{t('audit.table.role')}</th>
                    <th className="text-start font-medium pb-2">{t('audit.table.action')}</th>
                    <th className="text-start font-medium pb-2">{t('audit.table.resource')}</th>
                    <th className="text-start font-medium pb-2">{t('audit.table.ip')}</th>
                    <th className="text-start font-medium pb-2">{t('audit.table.outcome')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((e) => (
                    <tr key={e.id}>
                      <td className="py-2 num text-xs">{dateFmt.format(new Date(e.at))}</td>
                      <td className="py-2 text-xs" dir="ltr">{e.actor}</td>
                      <td className="py-2 text-xs">—</td>
                      <td className="py-2"><code className="text-xs">{e.action}</code></td>
                      <td className="py-2 text-xs">
                        <code className="text-[10px]">{e.resource}</code>
                      </td>
                      <td className="py-2 text-xs num">{e.ip ?? ''}</td>
                      <td className="py-2">
                        <Badge tone="success">{t('audit.table.ok')}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
